import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where, doc, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import './ProfileRewards.css';

const ProfileRewards = ({ userData }) => {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const [showWheelModal, setShowWheelModal] = useState(false);
  const [spinningWheel, setSpinningWheel] = useState(null);
  const [currentSpinIndex, setCurrentSpinIndex] = useState(0);
  const [wonItem, setWonItem] = useState(null);
  const [itemWidth, setItemWidth] = useState(150);
  const [showFullImage, setShowFullImage] = useState(false);
  const [fullImageUrl, setFullImageUrl] = useState('');
  const [showPrizesModal, setShowPrizesModal] = useState(false);
  const [selectedPrizes, setSelectedPrizes] = useState([]);
  const [selectedWheelReward, setSelectedWheelReward] = useState(null);
  const [isSpinning, setIsSpinning] = useState(false);

  useEffect(() => {
    loadRewards();
  }, [userData]);

  const loadRewards = async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return;

      // Get all active rewards
      const q = query(
        collection(db, 'rewards'),
        where('active', '==', true)
      );
      const snapshot = await getDocs(q);
      const allRewards = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter rewards the user can see
      const userRewards = allRewards.filter(reward => {
        if (reward.type === 'champion') {
          // User must be in the eligible players list
          return reward.eligibleUsers?.includes(user.uid);
        } else if (reward.type === 'regular') {
          // User must be in eligible list
          return reward.eligibleUsers?.includes(user.uid);
        } else if (reward.type === 'wheelFortune') {
          // User must be in eligible list for wheel
          return reward.eligibleUsers?.includes(user.uid);
        }
        return false;
      });

      setRewards(userRewards);
    } catch (error) {
      console.error('Error loading rewards:', error);
    } finally {
      setLoading(false);
    }
  };

  const canClaimChampionReward = (reward) => {
    const auth = getAuth();
    const user = auth.currentUser;
    const userRank = userData.rank;
    
    // Check if user is eligible (in the selected players list)
    if (reward.eligibleUsers && !reward.eligibleUsers.includes(user?.uid)) {
      return {
        canClaim: false,
        reason: 'You are not eligible for this reward'
      };
    }
    
    // Check if user already claimed
    const hasClaimed = reward.claimedBy?.some(claim => claim.userId === user?.uid);
    if (hasClaimed) {
      return {
        canClaim: false,
        reason: 'You already claimed your reward'
      };
    }

    // Check if there are items left
    const claimedCount = reward.claimedBy?.length || 0;
    if (claimedCount >= reward.items?.length) {
      return {
        canClaim: false,
        reason: 'All rewards have been claimed'
      };
    }

    // Get the eligible players sorted by rank and find next person who can claim
    const eligiblePlayersInfo = reward.eligiblePlayersInfo || [];
    if (eligiblePlayersInfo.length === 0) {
      return {
        canClaim: false,
        reason: 'Reward configuration error'
      };
    }

    // Get unclaimed eligible players sorted by rank
    const unclaimedPlayers = eligiblePlayersInfo.filter(player => 
      !reward.claimedBy?.some(claim => claim.userId === player.id)
    );

    if (unclaimedPlayers.length === 0) {
      return {
        canClaim: false,
        reason: 'All eligible players have claimed their rewards'
      };
    }

    // Check if it's this user's turn (should have the best rank among unclaimed)
    const nextPlayerToClaimRank = unclaimedPlayers[0].rank;
    if (userRank !== nextPlayerToClaimRank) {
      return {
        canClaim: false,
        reason: `Waiting for Rank #${nextPlayerToClaimRank} to claim first`
      };
    }

    return { canClaim: true };
  };

  const handleClaimChampionReward = async (reward, itemIndex) => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const canClaim = canClaimChampionReward(reward);
    if (!canClaim.canClaim) {
      toast.error(canClaim.reason);
      return;
    }

    const itemName = typeof reward.items[itemIndex] === 'string' 
      ? reward.items[itemIndex] 
      : reward.items[itemIndex].name;

    if (!window.confirm(`Claim "${itemName}"? This action cannot be undone.`)) {
      return;
    }

    setClaiming(true);
    try {
      const rewardRef = doc(db, 'rewards', reward.id);
      
      // Add claim record
      const claimRecord = {
        userId: user.uid,
        pseudo: userData.pseudo,
        rank: userData.rank,
        itemIndex: itemIndex,
        itemName: itemName,
        claimedAt: new Date().toISOString()
      };

      await updateDoc(rewardRef, {
        claimedBy: arrayUnion(claimRecord)
      });

      toast.success(`🎉 You claimed: ${reward.items[itemIndex]}`);
      loadRewards(); // Reload to update UI
    } catch (error) {
      console.error('Error claiming reward:', error);
      toast.error('Failed to claim reward. Please try again.');
    } finally {
      setClaiming(false);
    }
  };

  const handleClaimRegularReward = async (reward) => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    // Check if already claimed
    const hasClaimed = reward.claimedBy?.some(claim => claim.userId === user.uid);
    if (hasClaimed) {
      toast.error('You already claimed this reward');
      return;
    }

    if (!window.confirm(`Claim "${reward.name}"? This action cannot be undone.`)) {
      return;
    }

    setClaiming(true);
    try {
      const rewardRef = doc(db, 'rewards', reward.id);
      
      const claimRecord = {
        userId: user.uid,
        pseudo: userData.pseudo,
        claimedAt: new Date().toISOString()
      };

      await updateDoc(rewardRef, {
        claimedBy: arrayUnion(claimRecord)
      });

      toast.success(`🎉 You claimed: ${reward.name}`);
      loadRewards(); // Reload to update UI
    } catch (error) {
      console.error('Error claiming reward:', error);
      toast.error('Failed to claim reward. Please try again.');
    } finally {
      setClaiming(false);
    }
  };

  const canSpinWheel = (reward) => {
    const auth = getAuth();
    const user = auth.currentUser;
    const userRank = userData.rank;
    
    // Check if user is eligible (in the selected players list)
    if (reward.eligibleUsers && !reward.eligibleUsers.includes(user?.uid)) {
      return {
        canSpin: false,
        reason: 'You are not eligible for this reward'
      };
    }
    
    // Check if user already used all spins
    const spinsUsed = reward.spinsUsed?.[user?.uid] || 0;
    const maxSpins = reward.spinCount?.[user?.uid] || 1;
    if (spinsUsed >= maxSpins) {
      return {
        canSpin: false,
        reason: 'You have used all your spins'
      };
    }

    // Get available items (items that haven't been won yet)
    const availableItems = reward.items?.filter(item => {
      const wonCount = reward.wonItems?.[item.name] || 0;
      return wonCount < (item.quantity || 1);
    }) || [];

    if (availableItems.length === 0) {
      return {
        canSpin: false,
        reason: 'All items have been won'
      };
    }

    // Get the eligible players sorted by rank (HIGHER rank first for wheel)
    const eligiblePlayersInfo = reward.eligiblePlayersInfo || [];
    if (eligiblePlayersInfo.length === 0) {
      return {
        canSpin: false,
        reason: 'Reward configuration error'
      };
    }

    // Get players who haven't used all their spins, sorted by rank (ascending = higher rank first)
    const playersWithSpinsLeft = eligiblePlayersInfo.filter(player => {
      const playerSpins = reward.spinsUsed?.[player.id] || 0;
      const playerMaxSpins = reward.spinCount?.[player.id] || 1;
      return playerSpins < playerMaxSpins;
    }).sort((a, b) => a.rank - b.rank); // Lower number = higher rank

    if (playersWithSpinsLeft.length === 0) {
      return {
        canSpin: false,
        reason: 'All eligible players have used their spins'
      };
    }

    // Check if it's this user's turn (should have the best rank among players with spins left)
    const nextPlayerToSpinRank = playersWithSpinsLeft[0].rank;
    if (userRank !== nextPlayerToSpinRank) {
      return {
        canSpin: false,
        reason: `Waiting for Rank #${nextPlayerToSpinRank} to spin first`
      };
    }

    return { canSpin: true, availableItems };
  };

  const handleSpinWheel = async (reward) => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    // Check if user can spin
    const spinCheck = canSpinWheel(reward);
    if (!spinCheck.canSpin) {
      toast.error(spinCheck.reason);
      return;
    }

    const availableItems = spinCheck.availableItems;

    // Check how many spins user has used
    const spinsUsed = reward.spinsUsed?.[user.uid] || 0;
    const maxSpins = reward.spinCount?.[user.uid] || 1;

    setClaiming(true);
    setIsSpinning(true);
    
    // Calculate winner based on probability from AVAILABLE items only
    const items = availableItems;
    const totalWeight = items.reduce((sum, item) => sum + (item.probability || 10), 0);
    const random = Math.random() * totalWeight;
    
    let cumulativeWeight = 0;
    let selectedWonItem = items[0];
    
    for (const item of items) {
      cumulativeWeight += (item.probability || 10);
      if (random <= cumulativeWeight) {
        selectedWonItem = item;
        break;
      }
    }

    // SAVE TO DATABASE FIRST - before showing animation
    // This prevents abuse even if user closes app during animation
    try {
      const rewardRef = doc(db, 'rewards', reward.id);
      
      // Update spins used
      const newSpinsUsed = { ...reward.spinsUsed };
      newSpinsUsed[user.uid] = spinsUsed + 1;
      
      // Track won items count
      const newWonItems = { ...reward.wonItems };
      newWonItems[selectedWonItem.name] = (newWonItems[selectedWonItem.name] || 0) + 1;
      
      // Add to win history
      const winRecord = {
        userId: user.uid,
        pseudo: userData.pseudo,
        itemName: selectedWonItem.name,
        wonAt: new Date().toISOString(),
        rank: userData.rank
      };

      await updateDoc(rewardRef, {
        spinsUsed: newSpinsUsed,
        wonItems: newWonItems,
        winHistory: arrayUnion(winRecord)
      });
    } catch (error) {
      console.error('Error saving spin:', error);
      toast.error('Failed to process spin. Please try again.');
      setClaiming(false);
      setIsSpinning(false);
      return;
    }

    // Calculate and cache the item width before animation starts
    const width = window.innerWidth <= 480 ? 93 : window.innerWidth <= 768 ? 116 : 150;
    setItemWidth(width);

    // Show spinning animation using ALL items for visual consistency
    // But the winner is already determined from available items only
    setSpinningWheel(reward);
    setWonItem(null); // Don't show result yet
    setShowWheelModal(true);
    setCurrentSpinIndex(0);

    // Calculate which item was won from the FULL item list
    const allItems = reward.items || [];
    const wonIndex = allItems.findIndex(item => item.name === selectedWonItem.name);
    
    // Animate the spinning - we'll land on wonIndex at the end
    let spinCount = 0;
    const baseSpins = 20; // Minimum number of full cycles through all items
    const totalItemsToScroll = (baseSpins * allItems.length) + wonIndex; // End exactly on won item
    
    const spin = () => {
      spinCount++;
      setCurrentSpinIndex(spinCount);
      
      if (spinCount >= totalItemsToScroll) {
        // Stopped - show the won item
        setTimeout(() => {
          setWonItem(selectedWonItem);
          toast.success(`🎉 You won: ${selectedWonItem.name}!`);
          setClaiming(false);
          setIsSpinning(false);
          loadRewards();
        }, 500);
      } else {
        // Smooth linear deceleration from fast to slow
        const progress = spinCount / totalItemsToScroll;
        const easedSpeed = 20 + (progress * 200); // Linear: starts at 20ms, ends at 220ms
        
        // Continue spinning
        setTimeout(spin, easedSpeed);
      }
    };
    
    // Start the spin
    setTimeout(spin, 20);
  };

  const handleClaimItem = async (reward, itemIndex) => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const canClaim = canClaimChampionReward(reward);
    if (!canClaim.canClaim) {
      toast.error(canClaim.reason);
      return;
    }

    const itemName = typeof reward.items[itemIndex] === 'string' 
      ? reward.items[itemIndex] 
      : reward.items[itemIndex].name;

    if (!window.confirm(`Claim "${itemName}"? This action cannot be undone.`)) {
      return;
    }

    setClaiming(true);
    try {
      const rewardRef = doc(db, 'rewards', reward.id);
      
      // Add claim record
      const claimRecord = {
        userId: user.uid,
        pseudo: userData.pseudo,
        rank: userData.rank,
        itemIndex: itemIndex,
        itemName: itemName,
        claimedAt: new Date().toISOString()
      };

      await updateDoc(rewardRef, {
        claimedBy: arrayUnion(claimRecord)
      });

      toast.success(`🎉 You claimed: ${itemName}`);
      loadRewards(); // Reload to update UI
      setShowRewardModal(false); // Close modal after claiming
    } catch (error) {
      console.error('Error claiming reward:', error);
      toast.error('Failed to claim reward. Please try again.');
    } finally {
      setClaiming(false);
    }
  };

  const hasUserClaimed = (reward) => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return false;
    return reward.claimedBy?.some(claim => claim.userId === user.uid);
  };

  if (loading) {
    return (
      <div className="profile-section">
        <h3 className="section-title">Rewards</h3>
        <div className="rewards-loading">Loading rewards...</div>
      </div>
    );
  }

  if (rewards.length === 0) {
    return (
      <div className="profile-section">
        <h3 className="section-title">Rewards</h3>
        <div className="no-rewards">
          <p>No rewards available at the moment</p>
          <small>Keep climbing the ranks for exclusive rewards!</small>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="profile-section">
        <h3 className="section-title">Rewards</h3>
        
        <div className="rewards-list">
          {rewards.map((reward) => {
            const userClaimed = hasUserClaimed(reward);

          if (reward.type === 'champion') {
            const canClaimInfo = canClaimChampionReward(reward);
            const availableItems = reward.items?.filter((item, index) => {
              return !reward.claimedBy?.some(claim => claim.itemIndex === index);
            }) || [];

            // Determine the next player who can claim
            const eligiblePlayersInfo = reward.eligiblePlayersInfo || [];
            const unclaimedPlayers = eligiblePlayersInfo.filter(player => 
              !reward.claimedBy?.some(claim => claim.userId === player.id)
            );
            const nextPlayerRank = unclaimedPlayers.length > 0 ? unclaimedPlayers[0].rank : null;
            const isUserTurn = canClaimInfo.canClaim;

            return (
              <div key={reward.id} className="reward-card champion-reward">
                <img 
                  src={reward.imageUrl || '/default-reward.png'} 
                  alt={reward.name}
                  className="reward-icon"
                />
                
                <div className="reward-content">
                  <div className="reward-header-info">
                    <h4 className="reward-title">{reward.name}</h4>
                  </div>
                  <p className="reward-description">{reward.description}</p>
                  
                  <div className="reward-progress">
                    <small className={isUserTurn ? "can-claim-glow" : ""}>
                      {isUserTurn 
                        ? "You can claim your reward now!" 
                        : nextPlayerRank 
                          ? `Next to claim: Rank #${nextPlayerRank}` 
                          : "All items have been claimed"}
                    </small>
                  </div>
                </div>

                <div className="reward-action">
                  {userClaimed ? (
                    <div className="reward-claimed">
                      <span className="claimed-badge">Claimed</span>
                      <p className="claimed-text">
                        {reward.claimedBy.find(c => c.userId === getAuth().currentUser?.uid)?.itemName}
                      </p>
                    </div>
                  ) : (
                    <button
                      className="view-reward-btn"
                      onClick={() => {
                        setSelectedReward(reward);
                        setShowRewardModal(true);
                      }}
                    >
                      View Reward
                    </button>
                  )}
                </div>
              </div>
            );
          } else if (reward.type === 'wheelFortune') {
            // Wheel of Fortune reward
            const auth = getAuth();
            const user = auth.currentUser;
            const spinsUsed = reward.spinsUsed?.[user?.uid] || 0;
            const maxSpins = reward.spinCount?.[user?.uid] || 1;
            const spinsRemaining = maxSpins - spinsUsed;
            const spinCheck = canSpinWheel(reward);

            // Get available items count
            const availableItems = reward.items?.filter(item => {
              const wonCount = reward.wonItems?.[item.name] || 0;
              return wonCount < (item.quantity || 1);
            }) || [];

            // Determine next player who can spin
            const eligiblePlayersInfo = reward.eligiblePlayersInfo || [];
            const playersWithSpinsLeft = eligiblePlayersInfo.filter(player => {
              const playerSpins = reward.spinsUsed?.[player.id] || 0;
              const playerMaxSpins = reward.spinCount?.[player.id] || 1;
              return playerSpins < playerMaxSpins;
            }).sort((a, b) => a.rank - b.rank);
            const nextPlayerRank = playersWithSpinsLeft.length > 0 ? playersWithSpinsLeft[0].rank : null;
            const isUserTurn = spinCheck.canSpin;

            return (
              <div key={reward.id} className="reward-card wheel-reward">
                <img 
                  src={reward.imageUrl || 'https://i.imgur.com/F1dq7sr.png'} 
                  alt={reward.name}
                  className="reward-icon"
                />
                
                <div className="reward-content">
                  <div className="reward-header-info">
                    <h4 className="reward-title">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                        <circle cx="12" cy="12" r="2" fill="currentColor"/>
                        <line x1="12" y1="2" x2="12" y2="6" stroke="currentColor" strokeWidth="2"/>
                        <line x1="12" y1="18" x2="12" y2="22" stroke="currentColor" strokeWidth="2"/>
                        <line x1="2" y1="12" x2="6" y2="12" stroke="currentColor" strokeWidth="2"/>
                        <line x1="18" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="2"/>
                        <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" stroke="currentColor" strokeWidth="2"/>
                        <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" stroke="currentColor" strokeWidth="2"/>
                        <line x1="19.07" y1="4.93" x2="16.24" y2="7.76" stroke="currentColor" strokeWidth="2"/>
                        <line x1="7.76" y1="16.24" x2="4.93" y2="19.07" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      {reward.name}
                    </h4>
                  </div>
                  <p className="reward-description">{reward.description}</p>
                  
                  <div className="reward-progress">
                    <small>
                      Spins remaining: {spinsRemaining} / {maxSpins}
                    </small>
                    <small>
                      Items available: {availableItems.length} / {reward.items?.length || 0}
                    </small>
                    {!isUserTurn && nextPlayerRank && spinsRemaining > 0 && (
                      <small className="waiting-text">
                        Waiting for Rank #{nextPlayerRank} to spin
                      </small>
                    )}
                  </div>

                  <button 
                    className="view-prizes-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPrizes(availableItems);
                      setSelectedWheelReward(reward);
                      setShowPrizesModal(true);
                    }}
                  >
                    Spin the Wheel
                  </button>
                </div>
              </div>
            );
          } else {
            // Regular reward
            return (
              <div key={reward.id} className="reward-card regular-reward">
                <img 
                  src={reward.imageUrl || '/default-reward.png'} 
                  alt={reward.name}
                  className="reward-icon"
                />
                
                <div className="reward-content">
                  <div className="reward-header-info">
                    <h4 className="reward-title">{reward.name}</h4>
                  </div>
                  <p className="reward-description">{reward.description}</p>
                  
                  <div className="reward-progress">
                    <small>
                      {reward.claimedBy?.length || 0} player{(reward.claimedBy?.length || 0) !== 1 ? 's' : ''} claimed
                    </small>
                  </div>
                </div>

                <div className="reward-action">
                  {userClaimed ? (
                    <div className="reward-claimed">
                      <span className="claimed-badge">✓ Claimed</span>
                    </div>
                  ) : (
                    <button
                      className="claim-reward-btn"
                      onClick={() => handleClaimRegularReward(reward)}
                      disabled={claiming}
                    >
                      {claiming ? 'Claiming...' : '🎁 Claim'}
                    </button>
                  )}
                </div>
              </div>
            );
          }
        })}
      </div>
    </div>

    {/* Champion Reward Modal */}
    {showRewardModal && selectedReward && (
      <div className="reward-modal-overlay" onClick={() => setShowRewardModal(false)}>
        <div className="reward-modal-content" onClick={e => e.stopPropagation()}>
          <button className="modal-close-btn" onClick={() => setShowRewardModal(false)}>×</button>
          
          <div className="reward-modal-header">
            <h2>{selectedReward.name}</h2>
            <p className="reward-description">{selectedReward.description}</p>
          </div>

          <div className="modal-items-section">
            <h3>Available Items</h3>
            <div className="modal-items-grid">
              {selectedReward.items && selectedReward.items.length > 0 ? (
                selectedReward.items.map((item, index) => {
                  const itemName = typeof item === 'string' ? item : item.name;
                  const itemDescription = typeof item === 'object' ? item.description : '';
                  const itemImage = typeof item === 'object' ? item.image : '';
                  
                  const auth = getAuth();
                  const currentUser = auth.currentUser;
                  
                  // Check if this item is claimed by ANY user
                  const claimInfo = selectedReward.claimedBy?.find(claim => claim.itemIndex === index);
                  const isClaimedByAnyone = !!claimInfo;
                  const isClaimedByCurrentUser = claimInfo?.userId === currentUser?.uid;
                  
                  return (
                    <div key={index} className={`modal-item-card ${isClaimedByAnyone ? 'item-claimed' : ''}`}>
                      {isClaimedByAnyone && (
                        <div className="claimed-label">
                          Claimed {isClaimedByCurrentUser ? 'by you' : `by ${claimInfo.pseudo}`}
                        </div>
                      )}
                      <h4 className="modal-item-title">{itemName}</h4>
                      {itemImage && (
                        <div className="modal-item-image">
                          <img src={itemImage} alt={itemName} />
                        </div>
                      )}
                      {itemDescription && <p className="modal-item-description">{itemDescription}</p>}
                      <button
                        className={`modal-claim-btn ${isClaimedByAnyone ? 'claimed' : ''}`}
                        onClick={() => handleClaimItem(selectedReward, index)}
                        disabled={isClaimedByAnyone}
                      >
                        {isClaimedByAnyone ? '✓ Claimed' : 'Claim Item'}
                      </button>
                    </div>
                  );
                })
              ) : (
                <p>No items available</p>
              )}
            </div>
          </div>

          <button className="modal-done-btn" onClick={() => setShowRewardModal(false)}>Done</button>
        </div>
      </div>
    )}

    {/* Wheel Spinning Modal */}
    {showWheelModal && spinningWheel && (
      <div className="wheel-modal-overlay" onClick={() => !isSpinning && setShowWheelModal(false)}>
        <div className="wheel-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="wheel-header">
            <h2>🎡 {spinningWheel.name}</h2>
            <p>Spinning the wheel...</p>
          </div>

          <div className="wheel-container">
            <div className="wheel-items-wrapper-horizontal">
              <div className="wheel-highlight-overlay"></div>
              <div 
                className="wheel-items-scroll-horizontal"
                style={{
                  transform: `translateX(-${currentSpinIndex * itemWidth}px)`,
                  transition: 'none'
                }}
              >
                {/* Repeat items many times for seamless scrolling */}
                {[...Array(50)].map((_, repeatIndex) => 
                  spinningWheel.items.map((item, itemIndex) => {
                    return (
                      <div 
                        key={`${repeatIndex}-${itemIndex}`}
                        className="wheel-item-horizontal"
                      >
                        {item.image && (
                          <img src={item.image} alt={item.name} className="wheel-item-image" />
                        )}
                        <div className="wheel-item-name">{item.name}</div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            <div className="wheel-pointer-horizontal">▼</div>
          </div>

          {wonItem && (
            <div className="wheel-result">
              <div className="result-animation">
                <h3>You Won!</h3>
                <div className="won-item-display">
                  {wonItem.image && (
                    <img 
                      src={wonItem.image} 
                      alt={wonItem.name} 
                      className="won-item-image" 
                      onClick={() => {
                        setFullImageUrl(wonItem.image);
                        setShowFullImage(true);
                      }}
                      style={{ cursor: 'pointer' }}
                      title="Click to view full image"
                    />
                  )}
                  <div className="won-item-name">{wonItem.name}</div>
                  {wonItem.description && (
                    <div className="won-item-description">{wonItem.description}</div>
                  )}
                </div>
              </div>
              <button 
                className="wheel-close-btn"
                onClick={() => {
                  if (!isSpinning) {
                    setShowWheelModal(false);
                    setSpinningWheel(null);
                    setWonItem(null);
                    setIsSpinning(false);
                  }
                }}
                disabled={isSpinning}
                style={{ opacity: isSpinning ? 0.5 : 1, cursor: isSpinning ? 'not-allowed' : 'pointer' }}
              >
                {isSpinning ? 'Spinning...' : 'Close'}
              </button>
            </div>
          )}
        </div>
      </div>
    )}

    {/* Full Image Modal */}
    {showFullImage && (
      <div className="full-image-modal" onClick={() => setShowFullImage(false)}>
        <div className="full-image-content" onClick={(e) => e.stopPropagation()}>
          <button className="full-image-close" onClick={() => setShowFullImage(false)}>×</button>
          <img src={fullImageUrl} alt="Full size" className="full-image" />
        </div>
      </div>
    )}

    {/* Prizes Modal */}
    {showPrizesModal && (
      <div className="prizes-modal-overlay" onClick={() => !isSpinning && setShowPrizesModal(false)}>
        <div className="prizes-modal-content" onClick={(e) => e.stopPropagation()}>
          <button 
            className="modal-close-btn" 
            onClick={() => !isSpinning && setShowPrizesModal(false)}
            disabled={isSpinning}
            style={{ opacity: isSpinning ? 0.5 : 1, cursor: isSpinning ? 'not-allowed' : 'pointer' }}
          >×</button>
          
          <div className="prizes-modal-header">
            <h2>Possible Prizes</h2>
            <p>All items you can win from this wheel</p>
          </div>

          <div className="prizes-grid">
            {selectedPrizes.map((prize, index) => (
              <div key={index} className="prize-item">
                {prize.image && (
                  <div className="prize-item-image-wrapper">
                    <img 
                      src={prize.image} 
                      alt={prize.name} 
                      className="prize-item-image"
                      onClick={() => {
                        setFullImageUrl(prize.image);
                        setShowFullImage(true);
                      }}
                    />
                  </div>
                )}
                <div className="prize-item-name">{prize.name}</div>
                {prize.description && (
                  <div className="prize-item-description">{prize.description}</div>
                )}
              </div>
            ))}
          </div>

          {selectedWheelReward && (
            (() => {
              const auth = getAuth();
              const user = auth.currentUser;
              const spinsUsed = selectedWheelReward.spinsUsed?.[user?.uid] || 0;
              const maxSpins = selectedWheelReward.spinCount?.[user?.uid] || 1;
              const hasSpinsLeft = spinsUsed < maxSpins;
              const spinCheck = canSpinWheel(selectedWheelReward);
              const canSpin = spinCheck.canSpin;

              let buttonText = 'SPIN THE WHEEL';
              if (claiming) {
                buttonText = 'Spinning...';
              } else if (!hasSpinsLeft) {
                buttonText = 'NO SPINS LEFT';
              } else if (!canSpin) {
                // Check if it's a waiting message
                if (spinCheck.reason?.includes('Waiting for Rank')) {
                  buttonText = 'Wait...';
                } else {
                  buttonText = 'CANNOT SPIN';
                }
              }

              return (
                <button 
                  className="prizes-modal-spin-btn"
                  onClick={() => {
                    setShowPrizesModal(false);
                    handleSpinWheel(selectedWheelReward);
                  }}
                  disabled={claiming || !hasSpinsLeft || !canSpin}
                >
                  {buttonText}
                </button>
              );
            })()
          )}
        </div>
      </div>
    )}
    </>
  );
};

export default ProfileRewards;
